// Main-thread handle to the solver Web Worker. One job at a time: starting a new job cancels the
// previous one.

import type { ReachRequest, RegionRequest, SolverResponse, SolverResult } from './protocol';

export type Progress = { phase: string; done: number; total: number };

type Pending = {
  resolve: (r: SolverResult | null) => void;
  reject: (e: Error) => void;
  onProgress?: (p: Progress) => void;
};

export class SolverClient {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<number, Pending>();

  constructor() {
    this.worker = this.spawn();
  }

  private spawn(): Worker {
    const w = new Worker(new URL('./solver.worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (ev: MessageEvent<SolverResponse>) => {
      const m = ev.data;
      const p = this.pending.get(m.id);
      if (!p) return;
      if (m.kind === 'progress') return p.onProgress?.({ phase: m.phase, done: m.done, total: m.total });
      this.pending.delete(m.id);
      if (m.kind === 'result') p.resolve(m.result);
      else if (m.kind === 'cancelled') p.resolve(null);
      else p.reject(new Error(m.message));
    };
    w.onerror = (e) => {
      for (const p of this.pending.values()) p.reject(new Error(e.message || 'Solver crashed'));
      this.pending.clear();
      this.worker = this.spawn();
    };
    return w;
  }

  /** Run a job; resolves to null if cancelled. */
  run<R extends SolverResult>(req: Omit<ReachRequest, 'id'> | Omit<RegionRequest, 'id'>, onProgress?: (p: Progress) => void): Promise<R | null> {
    this.cancelAll();
    const id = this.nextId++;
    return new Promise<R | null>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (r: SolverResult | null) => void, reject, onProgress });
      this.worker.postMessage({ ...req, id });
    });
  }

  cancelAll(): void {
    for (const id of this.pending.keys()) this.worker.postMessage({ type: 'cancel', id });
  }
}
